import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {loadStripe, Stripe, StripeCardElement, StripeElements} from '@stripe/stripe-js';
import {PaymentService} from './payment.service';
import {OrderService, OrderView} from '../shared/order.service';
import {TokenStorageService} from '../_services/token-storage.service';
import {HttpClient} from '@angular/common/http';
import {from, switchMap} from 'rxjs';
import {environment} from '../../environments/environment';
import {BreadcrumbService} from 'xng-breadcrumb';

@Component({
  standalone: false,
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.css']
})
export class PaymentComponent implements OnInit {
  orderId = '';
  order: OrderView | null = null;
  stripe: Stripe | null = null;
  cardElement: StripeCardElement | null = null;
  elements: StripeElements | null = null;
  clientSecret = '';
  paymentIntentId = '';
  mode: 'mock' | 'stripe' = 'mock';
  error = '';
  paying = false;
  ready = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private paymentService: PaymentService,
    private orderService: OrderService,
    private tokens: TokenStorageService,
    private http: HttpClient,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.set('@Payment', 'Payment');
    if (!this.tokens.getToken()) {
      this.router.navigate(['/login']);
      return;
    }
    this.orderId = this.route.snapshot.queryParamMap.get('orderId') || '';
    if (!this.orderId) {
      this.error = 'Missing order.';
      return;
    }
    this.orderService.getOrder(this.orderId).subscribe({
      next: order => {
        this.order = order;
        this.startIntent();
      },
      error: err => {
        this.error = err?.error?.error || 'Order not found.';
      }
    });
  }

  async processPayment(): Promise<void> {
    this.error = '';
    this.paying = true;
    try {
      if (this.mode === 'mock') {
        await this.paymentService.confirmPayment(this.paymentIntentId).toPromise();
        this.router.navigate(['/orders']);
        return;
      }
      if (!this.stripe || !this.clientSecret) {
        this.error = 'Payment is not ready.';
        this.paying = false;
        return;
      }
      const {error, paymentIntent} = await this.stripe.confirmCardPayment(this.clientSecret, {
        payment_method: {card: this.cardElement!}
      });
      if (error) {
        this.error = error.message || 'Payment failed.';
        this.paying = false;
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        await this.paymentService.confirmPayment(paymentIntent.id).toPromise();
        this.router.navigate(['/orders']);
      }
    } catch (e: any) {
      this.error = e?.error?.error || e?.message || 'Payment failed.';
      this.paying = false;
    }
  }

  get amountLabel(): string {
    if (!this.order) {
      return '';
    }
    return `${(this.order.amountCents / 100).toFixed(2)} ${this.order.currency?.toUpperCase() || ''}`;
  }

  private startIntent(): void {
    this.paymentService.createPaymentIntent(this.orderId).subscribe({
      next: async (res) => {
        this.mode = res.mode === 'stripe' ? 'stripe' : 'mock';
        this.clientSecret = res.clientSecret;
        this.paymentIntentId = res.paymentIntentId;
        if (this.mode === 'stripe') {
          await this.mountCard();
        }
        this.ready = true;
      },
      error: err => {
        this.error = err?.error?.error || 'Could not start payment.';
      }
    });
  }

  private async mountCard(): Promise<void> {
    const key = await from(environment).pipe(
      switchMap((env: any) => this.http.get<any>(`${env.backendURL}/api/config`))
    ).toPromise().then(c => c?.stripePublishableKey || '');

    if (!key) {
      this.error = 'Stripe publishable key is not configured.';
      return;
    }
    this.stripe = await loadStripe(key);
    if (!this.stripe) {
      this.error = 'Could not load Stripe.';
      return;
    }
    this.elements = this.stripe.elements();
    this.cardElement = this.elements.create('card');
    this.cardElement.mount('#card-element');
  }
}
